<template>
  <v-container fluid>
    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="header-card" elevation="4">
          <v-card-title class="pa-6">
            <v-icon large color="white" class="mr-3">mdi-chart-timeline-variant</v-icon>
            <div>
              <h2 class="white--text">Dashboard สถิติเชิงลึกอัตรากำลังสถานศึกษา</h2>
              <p class="subtitle-1 mb-0 white--text" style="opacity: 0.9;">ระบบวิเคราะห์ข้อมูลบุคลากรแบบครอบคลุม</p>
            </div>
            <v-spacer></v-spacer>
            <v-btn color="white" text @click="refreshData" :loading="loading">
              <v-icon left>mdi-refresh</v-icon>
              รีเฟรช
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Advanced Filter Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="filter-card" elevation="3">
          <v-card-title class="filter-header white--text">
            <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
            ตัวกรองข้อมูลขั้นสูง
            <v-spacer></v-spacer>
            <v-btn icon color="white" @click="toggleFilters">
              <v-icon>{{ showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </v-card-title>
          
          <v-expand-transition>
            <v-card-text v-show="showFilters" class="pa-4">
              <v-row>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.region"
                    :items="regionOptions"
                    label="ภูมิภาค"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-map"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.province"
                    :items="provinceOptions"
                    label="จังหวัด"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-map-marker"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.personnelType"
                    :items="personnelTypeOptions"
                    label="ประเภทบุคลากร"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-account-tie"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.budget"
                    :items="budgetOptions"
                    label="งบประมาณ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-currency-usd"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.nationality"
                    :items="nationalityOptions"
                    label="สัญชาติ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-flag"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.level"
                    :items="levelOptions"
                    label="ระดับ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-stairs"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.education"
                    :items="educationOptions"
                    label="วุฒิการศึกษา"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-school"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.workgroup"
                    :items="workgroupOptions"
                    label="กลุ่มงาน"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-account-group"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.subject"
                    :items="subjectOptions"
                    label="วิชาเอก"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-book-open"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
                
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.salaryRange"
                    :items="salaryRangeOptions"
                    label="ช่วงเงินเดือน"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-cash"
                    @change="onFilterChange"
                  ></v-select>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <v-btn color="primary" @click="applyFilters" class="mr-2">
                    <v-icon left>mdi-filter-check</v-icon>
                    ใช้ตัวกรอง
                  </v-btn>
                  <v-btn color="grey" outlined @click="resetFilters" class="mr-2">
                    <v-icon left>mdi-filter-remove</v-icon>
                    ล้างตัวกรอง
                  </v-btn>
                  <v-btn color="success" @click="exportReport" class="mr-2">
                    <v-icon left>mdi-file-excel</v-icon>
                    ส่งออกรายงาน
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <!-- Advanced Summary Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="summary-card dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="40" color="white" class="mb-2">mdi-account-multiple</v-icon>
            <h3 class="stat-number">{{ summaryData.totalPersonnel | numberFormat }}</h3>
            <p class="stat-label mb-0">จำนวนบุคลากรทั้งหมด</p>
            <small class="growth-indicator">
              {{ summaryData.personnelGrowth > 0 ? '+' : '' }}{{ summaryData.personnelGrowth }}% จากปีที่แล้ว
            </small>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="region-card dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="40" color="white" class="mb-2">mdi-map</v-icon>
            <h3 class="stat-number">{{ summaryData.totalInstitutions | numberFormat }}</h3>
            <p class="stat-label mb-0">สถานศึกษาที่เข้าร่วม</p>
            <small class="growth-indicator">
              ครอบคลุม {{ summaryData.totalRegions }} ภูมิภาค
            </small>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="education-card dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="40" color="white" class="mb-2">mdi-school</v-icon>
            <h3 class="stat-number">{{ summaryData.avgSalary | currencyFormat }}</h3>
            <p class="stat-label mb-0">เงินเดือนเฉลี่ย</p>
            <small class="growth-indicator">
              สูงสุด {{ summaryData.maxSalary | currencyFormat }}
            </small>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="budget-card dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="40" color="white" class="mb-2">mdi-account-clock</v-icon>
            <h3 class="stat-number">{{ summaryData.avgExperience }}</h3>
            <p class="stat-label mb-0">อายุงานเฉลี่ย (ปี)</p>
            <small class="growth-indicator">
              อัตราคงอยู่ {{ summaryData.retentionRate }}%
            </small>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Additional Insights Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="insight-card-1 dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="30" color="white" class="mb-2">mdi-certificate</v-icon>
            <h4 class="insight-number">{{ summaryData.licensedPersonnel }}%</h4>
            <p class="insight-label mb-0">มีใบอนุญาต</p>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="insight-card-2 dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="30" color="white" class="mb-2">mdi-gender-male-female</v-icon>
            <h4 class="insight-number">{{ summaryData.genderRatio }}</h4>
            <p class="insight-label mb-0">อัตราส่วนเพศ (ช:ญ)</p>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="insight-card-3 dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="30" color="white" class="mb-2">mdi-star</v-icon>
            <h4 class="insight-number">{{ summaryData.avgRating }}/10</h4>
            <p class="insight-label mb-0">คะแนนประเมินเฉลี่ย</p>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="insight-card-4 dashboard-card" elevation="6">
          <v-card-text class="pa-4 text-center">
            <v-icon size="30" color="white" class="mb-2">mdi-trending-up</v-icon>
            <h4 class="insight-number">{{ summaryData.promotionRate }}%</h4>
            <p class="insight-label mb-0">อัตราการเลื่อนตำแหน่ง</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section Row 1 -->
    <v-row>
      <!-- Personnel Distribution by Region -->
      <v-col cols="12" lg="6">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-map-marker-multiple</v-icon>
            การกระจายบุคลากรตามภูมิภาค
            <v-spacer></v-spacer>
            <v-btn icon small @click="toggleChartType('region')">
              <v-icon>{{ regionChartType === 'pie' ? 'mdi-chart-bar' : 'mdi-chart-pie' }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="regionChartData.length > 0">
              <apexchart
                :type="regionChartType"
                height="350"
                :options="regionChartOptions"
                :series="regionChartType === 'pie' ? regionChartData : regionChartSeries"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- Salary Distribution -->
      <v-col cols="12" lg="6">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
            การกระจายเงินเดือน
          </v-card-title>
          <v-card-text>
            <div v-if="salaryDistributionSeries.length > 0">
              <apexchart
                type="histogram"
                height="350"
                :options="salaryDistributionOptions"
                :series="salaryDistributionSeries"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section Row 2 -->
    <v-row class="mt-4">
      <!-- Education Analysis -->
      <v-col cols="12" lg="8">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-school-outline</v-icon>
            การวิเคราะห์วุฒิการศึกษาและสาขา
          </v-card-title>
          <v-card-text>
            <v-tabs v-model="educationTab" color="primary">
              <v-tab>วุฒิการศึกษา</v-tab>
              <v-tab>สาขาวิชา</v-tab>
              <v-tab>วิชาเอก</v-tab>
            </v-tabs>
            <v-tabs-items v-model="educationTab">
              <v-tab-item>
                <div v-if="educationChartSeries.length > 0" class="mt-4">
                  <apexchart
                    type="bar"
                    height="350"
                    :options="educationChartOptions"
                    :series="educationChartSeries"
                  ></apexchart>
                </div>
              </v-tab-item>
              <v-tab-item>
                <div v-if="branchChartSeries.length > 0" class="mt-4">
                  <apexchart
                    type="treemap"
                    height="350"
                    :options="branchChartOptions"
                    :series="branchChartSeries"
                  ></apexchart>
                </div>
              </v-tab-item>
              <v-tab-item>
                <div v-if="subjectChartSeries.length > 0" class="mt-4">
                  <apexchart
                    type="donut"
                    height="350"
                    :options="subjectChartOptions"
                    :series="subjectChartData"
                  ></apexchart>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- Age and Experience Analysis -->
      <v-col cols="12" lg="4">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-account-clock-outline</v-icon>
            อายุและประสบการณ์
          </v-card-title>
          <v-card-text>
            <div v-if="ageExperienceData.length > 0">
              <apexchart
                type="scatter"
                height="350"
                :options="ageExperienceOptions"
                :series="ageExperienceData"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section Row 3 -->
    <v-row class="mt-4">
      <!-- Personnel Type and Budget Analysis -->
      <v-col cols="12" lg="6">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-account-group-outline</v-icon>
            ประเภทบุคลากรและงบประมาณ
          </v-card-title>
          <v-card-text>
            <div v-if="personnelBudgetSeries.length > 0">
              <apexchart
                type="bar"
                height="350"
                :options="personnelBudgetOptions"
                :series="personnelBudgetSeries"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- License Status -->
      <v-col cols="12" lg="6">
        <v-card class="chart-container dashboard-card" elevation="4">
          <v-card-title class="pb-2">
            <v-icon color="primary" class="mr-2">mdi-certificate-outline</v-icon>
            สถานะใบอนุญาตประกอบวิชาชีพ
          </v-card-title>
          <v-card-text>
            <div v-if="licenseStatusData.length > 0">
              <apexchart
                type="radialBar"
                height="350"
                :options="licenseStatusOptions"
                :series="licenseStatusData"
              ></apexchart>
            </div>
            <div v-else class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">กำลังโหลดข้อมูล...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'AdvancedManpowerDashboard',
  components: {
    apexchart: VueApexCharts,
  },
  
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      showFilters: true,
      dateMenu: false,
      search: '',
      educationTab: 0,
      regionChartType: 'pie',
      
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      
      // Enhanced Filters
      filters: {
        region: null,
        province: null,
        personnelType: null,
        budget: null,
        education: null,
        level: null,
        workgroup: null,
        subject: null,
        nationality: null,
        salaryRange: null,
        dateRange: []
      },
      
      // Raw data
      personnelData: [],
      
      // Enhanced Summary data
      summaryData: {
        totalPersonnel: 0,
        totalInstitutions: 0,
        totalRegions: 0,
        avgSalary: 0,
        maxSalary: 0,
        avgExperience: 0,
        retentionRate: 95,
        personnelGrowth: 5.2,
        licensedPersonnel: 87,
        genderRatio: '1.2:1',
        avgRating: 8.5,
        promotionRate: 12
      },
      
      
      // Chart options and data
      regionChartOptions: {
        chart: {
          type: 'pie',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        labels: [],
        colors: ['#B3151B', '#424242', '#E57373', '#9E9E9E', '#8E1014', '#BDBDBD', '#212121', '#F1B0B0'],
        legend: { position: 'bottom', fontSize: '14px' },
        plotOptions: {
          pie: { donut: { size: '0%' } }
        },
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      regionChartData: [],
      regionChartSeries: [],
      
      // Salary Distribution Chart
      salaryDistributionOptions: {
        chart: {
          type: 'bar',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: { horizontal: false, columnWidth: '70%', borderRadius: 4 }
        },
        xaxis: {
          categories: ['<20K', '20-30K', '30-40K', '40-50K', '50-60K', '>60K'],
          title: { text: 'ช่วงเงินเดือน (บาท)' }
        },
        yaxis: {
          title: { text: 'จำนวน (คน)' }
        },
        colors: ['#B3151B'],
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      salaryDistributionSeries: [],
      
      // Education Chart
      educationChartOptions: {
        chart: {
          type: 'bar',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4 }
        },
        xaxis: {
          title: { text: 'จำนวน (คน)' }
        },
        colors: ['#B3151B'],
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      educationChartSeries: [],
      
      // Branch Chart (Treemap)
      branchChartOptions: {
        chart: {
          type: 'treemap',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        colors: ['#B3151B', '#424242', '#E57373', '#9E9E9E', '#8E1014'],
        plotOptions: {
          treemap: {
            enableShades: true,
            shadeIntensity: 0.5,
            colorScale: {
              ranges: [
                { from: -6, to: 0, color: '#A86400' },
                { from: 0.001, to: 6, color: '#455A64' }
              ]
            }
          }
        },
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      branchChartSeries: [],
      
      // Subject Chart
      subjectChartOptions: {
        chart: {
          type: 'donut',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        labels: [],
        colors: ['#B3151B', '#424242', '#E57373', '#9E9E9E', '#8E1014'],
        legend: { position: 'bottom' },
        plotOptions: {
          pie: { donut: { size: '60%' } }
        },
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      subjectChartData: [],
      subjectChartSeries: [],
      
      // Age Experience Chart
      ageExperienceOptions: {
        chart: {
          type: 'scatter',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        xaxis: {
          title: { text: 'อายุ (ปี)' },
          min: 20,
          max: 70
        },
        yaxis: {
          title: { text: 'ประสบการณ์ (ปี)' },
          min: 0,
          max: 40
        },
        colors: ['#B3151B'],
        tooltip: {
          custom: function({ series, seriesIndex, dataPointIndex, w }) {
            const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
            return '<div class="custom-tooltip"><strong>อายุ:</strong> ' + data[0] + ' ปี<br><strong>ประสบการณ์:</strong> ' + data[1] + ' ปี</div>';
          }
        }
      },
      ageExperienceData: [],
      
      // Personnel Budget Chart
      personnelBudgetOptions: {
        chart: {
          type: 'bar',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false },
          stacked: true
        },
        plotOptions: {
          bar: { horizontal: false, borderRadius: 4 }
        },
        xaxis: {
          categories: []
        },
        yaxis: {
          title: { text: 'จำนวน (คน)' }
        },
        colors: ['#B3151B', '#424242', '#E57373'],
        legend: {
          position: 'top'
        },
        tooltip: {
          y: { formatter: function (val) { return val + " คน" } }
        }
      },
      personnelBudgetSeries: [],
      
      // License Status Chart
      licenseStatusOptions: {
        chart: {
          type: 'radialBar',
          fontFamily: 'Kanit, sans-serif',
          toolbar: { show: false }
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent'
            },
            dataLabels: {
              name: { show: false },
              value: { show: false }
            }
          }
        },
        colors: ['#B3151B', '#424242', '#E57373', '#9E9E9E'],
        labels: ['ใบอนุญาตใช้ได้', 'ใบอนุญาตหมดอายุ', 'รอการต่ออายุ', 'ไม่มีใบอนุญาต'],
        legend: {
          show: true,
          floating: true,
          fontSize: '14px',
          position: 'left',
          offsetX: 50,
          offsetY: 10
        }
      },
      licenseStatusData: []
    }
  },
  
  computed: {
    // Enhanced Filter options
    regionOptions() {
      const regions = [...new Set(this.personnelData.map(item => item.region_name).filter(Boolean))]
      return regions.map(region => ({ text: region, value: region }))
    },
    
    provinceOptions() {
      const provinces = [...new Set(this.personnelData.map(item => item.province_name).filter(Boolean))]
      return provinces.map(province => ({ text: province, value: province }))
    },
    
    personnelTypeOptions() {
      const types = [...new Set(this.personnelData.map(item => item.personnel_etc_type).filter(Boolean))]
      return types.map(type => ({ text: type, value: type }))
    },
    
    budgetOptions() {
      const budgets = [...new Set(this.personnelData.map(item => item.personnel_etc_budget).filter(Boolean))]
      return budgets.map(budget => ({ text: budget, value: budget }))
    },
    
    educationOptions() {
      const educations = [...new Set(this.personnelData.map(item => item.personnel_etc_education_1).filter(Boolean))]
      return educations.map(edu => ({ text: edu, value: edu }))
    },
    
    levelOptions() {
      const levels = [...new Set(this.personnelData.map(item => item.personnel_etc_level).filter(Boolean))]
      return levels.map(level => ({ text: level, value: level }))
    },
    
    workgroupOptions() {
      const workgroups = [...new Set(this.personnelData.map(item => item.personnel_etc_workgroup).filter(Boolean))]
      return workgroups.map(wg => ({ text: wg, value: wg }))
    },
    
    subjectOptions() {
      const subjects = [...new Set(this.personnelData.map(item => item.personnel_etc_subject).filter(Boolean))]
      return subjects.map(subject => ({ text: subject, value: subject }))
    },
    
    nationalityOptions() {
      const nationalities = [...new Set(this.personnelData.map(item => item.typeNationalityS).filter(Boolean))]
      return nationalities.map(nationality => ({ text: nationality, value: nationality }))
    },
    
    salaryRangeOptions() {
      return [
        { text: 'ต่ำกว่า 20,000', value: 'below_20k' },
        { text: '20,000 - 30,000', value: '20k_30k' },
        { text: '30,000 - 40,000', value: '30k_40k' },
        { text: '40,000 - 50,000', value: '40k_50k' },
        { text: '50,000 - 60,000', value: '50k_60k' },
        { text: 'มากกว่า 60,000', value: 'above_60k' }
      ]
    },
    
    // Enhanced Filtered data
    filteredPersonnelData() {
      let filtered = [...this.personnelData]
      
      // Apply all filters
      if (this.filters.region) {
        filtered = filtered.filter(item => item.region_name === this.filters.region)
      }
      
      if (this.filters.province) {
        filtered = filtered.filter(item => item.province_name === this.filters.province)
      }
      
      if (this.filters.personnelType) {
        filtered = filtered.filter(item => item.personnel_etc_type === this.filters.personnelType)
      }
      
      if (this.filters.budget) {
        filtered = filtered.filter(item => item.personnel_etc_budget === this.filters.budget)
      }
      
      if (this.filters.education) {
        filtered = filtered.filter(item => item.personnel_etc_education_1 === this.filters.education)
      }
      
      if (this.filters.level) {
        filtered = filtered.filter(item => item.personnel_etc_level === this.filters.level)
      }
      
      if (this.filters.workgroup) {
        filtered = filtered.filter(item => item.personnel_etc_workgroup === this.filters.workgroup)
      }
      
      if (this.filters.subject) {
        filtered = filtered.filter(item => item.personnel_etc_subject === this.filters.subject)
      }
      
      if (this.filters.nationality) {
        filtered = filtered.filter(item => item.typeNationalityS === this.filters.nationality)
      }
      
      if (this.filters.salaryRange) {
        filtered = filtered.filter(item => this.isInSalaryRange(item.personnel_etc_salary, this.filters.salaryRange))
      }
      
      // Add computed fields
      return filtered.map(item => ({
        ...item,
        fullName: `${item.personnel_etc_title || ''} ${item.personnel_etc_fristname || ''} ${item.personnel_etc_lastname || ''}`.trim(),
        experience: this.calculateExperience(item.personnel_etc_dateapp),
        license_status: this.getLicenseStatus(item)
      }))
    }
  },
  
  filters: {
    numberFormat(value) {
      if (!value && value !== 0) return '0'
      return new Intl.NumberFormat('th-TH').format(value)
    },
    
    currencyFormat(value) {
      if (!value && value !== 0) return '฿0'
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 0
      }).format(value)
    },
    
    dateFormat(value) {
      if (!value) return '-'
      try {
        return new Date(value).toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (error) {
        return '-'
      }
    }
  },
  
  async mounted() {
    await this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      try {
        // API call - ใช้ข้อมูล query ที่ให้มา
        const response = await this.$http.post('personnel_etc_dashboard.php', {
          ApiKey: this.ApiKey
        })
        this.personnelData = response.data || []
        
        this.updateSummaryData()
        this.updateAllCharts()
        this.showNotification('โหลดข้อมูลสำเร็จ', 'success')
      } catch (error) {
        console.error('Error loading data:', error)
        this.personnelData = this.generateEnhancedSampleData()
        this.updateSummaryData()
        this.updateAllCharts()
        this.showNotification('ใช้ข้อมูลตัวอย่าง', 'info')
      } finally {
        this.loading = false
      }
    },
    
    generateEnhancedSampleData() {
      const regions = ['กรุงเทพฯ', 'ภาคเหนือ', 'ภาคตะวันออกเฉียงเหนือ', 'ภาคกลาง', 'ภาคใต้']
      const provinces = ['กรุงเทพฯ', 'เชียงใหม่', 'ขอนแก่น', 'นครปฐม', 'สงขลา', 'ภูเก็ต', 'อุบลราชธานี']
      const personnelTypes = ['ข้าราชการครูและบุคลากรทางการศึกษา', 'พนักงานราชการ', 'ลูกจ้างประจำ', 'ลูกจ้างชั่วคราว']
      const educations = ['มัธยมศึกษาตอนปลาย', 'ประกาศนียบัตรวิชาชีพ', 'ประกาศนียบัตรวิชาชีพชั้นสูง', 'ปริญญาตรี', 'ปริญญาโท', 'ปริญญาเอก']
      const positions = ['ครูผู้ช่วย', 'ครู', 'อาจารย์', 'เจ้าหน้าที่', 'หัวหน้างาน', 'รองผู้อำนวยการ', 'ผู้อำนวยการ']
      const subjects = ['คณิตศาสตร์', 'วิทยาศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ', 'สังคมศึกษา', 'ศิลปะ', 'พลศึกษา', 'คอมพิวเตอร์']
      const branches = ['การศึกษา', 'วิทยาศาสตร์', 'ศิลปศาสตร์', 'วิศวกรรมศาสตร์', 'บริหารธุรกิจ', 'เทคโนโลยี']
      const budgets = ['งบประมาณแผ่นดิน', 'งบประมาณเงินรายได้', 'งบประมาณอื่นๆ']
      const nationalities = ['ไทย', 'พม่า', 'ลาว', 'กัมพูชา', 'อื่นๆ']
      
      const sampleData = []
      for (let i = 0; i < 200; i++) {
        const region = regions[Math.floor(Math.random() * regions.length)]
        const province = provinces[Math.floor(Math.random() * provinces.length)]
        const appointDate = new Date(2015 + Math.floor(Math.random() * 9), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
        const birthDate = new Date(1970 + Math.floor(Math.random() * 30), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
        
        sampleData.push({
          personnel_etc_college_code: `C${String(i + 1).padStart(4, '0')}`,
          typeNationalityS: nationalities[Math.floor(Math.random() * nationalities.length)],
          personnel_etc_m_type: Math.random() > 0.5 ? 'ชาย' : 'หญิง',
          personnel_etc_id_card: `${Math.floor(Math.random() * 9000000000000) + 1000000000000}`,
          personnel_etc_title: Math.random() > 0.5 ? 'นาย' : Math.random() > 0.5 ? 'นาง' : 'นางสาว',
          personnel_etc_fristname: `ชื่อ${i + 1}`,
          personnel_etc_lastname: `นามสกุล${i + 1}`,
          personnel_etc_id_position: positions[Math.floor(Math.random() * positions.length)],
          personnel_etc_level: Math.floor(Math.random() * 10) + 1,
          personnel_etc_rang: `C${Math.floor(Math.random() * 5) + 1}`,
          personnel_etc_type: personnelTypes[Math.floor(Math.random() * personnelTypes.length)],
          personnel_etc_about: `รายละเอียดเกี่ยวกับบุคลากรคนที่ ${i + 1}`,
          personnel_etc_budget: budgets[Math.floor(Math.random() * budgets.length)],
          personnel_etc_salary: Math.floor(Math.random() * 60000) + 20000,
          personnel_etc_workgroup: `กลุ่มงาน${Math.floor(Math.random() * 8) + 1}`,
          personnel_etc_education_1: educations[Math.floor(Math.random() * educations.length)],
          personnel_etc_education_2: Math.random() > 0.7 ? educations[Math.floor(Math.random() * educations.length)] : null,
          personnel_etc_education_3: Math.random() > 0.9 ? educations[Math.floor(Math.random() * educations.length)] : null,
          personnel_etc_education_4: null,
          personnel_etc_subject: subjects[Math.floor(Math.random() * subjects.length)],
          personnel_etc_branch: branches[Math.floor(Math.random() * branches.length)],
          personnel_etc_branch_sub: `สาขาย่อย${Math.floor(Math.random() * 5) + 1}`,
          personnel_etc_comment: Math.random() > 0.8 ? `หมายเหตุสำหรับบุคลากรคนที่ ${i + 1}` : null,
          personnel_etc_idplan: `P${String(i + 1).padStart(3, '0')}`,
          personnel_etc_datebrith: birthDate.toISOString().split('T')[0],
          personnel_etc_dateapp: appointDate.toISOString().split('T')[0],
          personnel_etc_tel: `0${Math.floor(Math.random() * 900000000) + 100000000}`,
          personnel_etc_email: Math.random() > 0.3 ? `user${i + 1}@email.com` : null,
          personnel_etc_pic: null,
          personnel_etc_licence: Math.random() > 0.2 ? `LIC${String(i + 1).padStart(4, '0')}` : null,
          personnel_etc_onlicence: Math.random() > 0.2 ? new Date(2020 + Math.floor(Math.random() * 4), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0] : null,
          personnel_etc_endlicence: Math.random() > 0.2 ? new Date(2025 + Math.floor(Math.random() * 5), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0] : null,
          personnel_etc_licencefile: Math.random() > 0.5 ? `license_${i + 1}.pdf` : null,
          personnel_etc_province: province,
          personnel_etc_safety: Math.random() > 0.8 ? 'ผ่านการอบรม' : 'ยังไม่ผ่าน',
          personnel_etc_budgetse: Math.floor(Math.random() * 100000) + 50000,
          personnel_etc_budgetetc: Math.floor(Math.random() * 50000) + 10000,
          province_ID: Math.floor(Math.random() * 77) + 1,
          province_name: province,
          region_ID: Math.floor(Math.random() * 5) + 1,
          region_name: region
        })
      }
      return sampleData
    },
    
    // Enhanced Summary Data Calculation
    updateSummaryData() {
      const filteredData = this.filteredPersonnelData
      const salaries = filteredData.map(item => item.personnel_etc_salary || 0).filter(s => s > 0)
      const experiences = filteredData.map(item => item.experience || 0).filter(e => e >= 0)
      
      this.summaryData = {
        totalPersonnel: filteredData.length,
        totalInstitutions: [...new Set(filteredData.map(item => item.personnel_etc_college_code))].length,
        totalRegions: [...new Set(filteredData.map(item => item.region_name))].length,
        avgSalary: salaries.length ? Math.round(salaries.reduce((a, b) => a + b, 0) / salaries.length) : 0,
        maxSalary: salaries.length ? Math.max(...salaries) : 0,
        avgExperience: experiences.length ? Math.round(experiences.reduce((a, b) => a + b, 0) / experiences.length * 10) / 10 : 0,
        retentionRate: 95,
        personnelGrowth: 5.2,
        licensedPersonnel: Math.round((filteredData.filter(item => item.license_status === 'valid').length / filteredData.length) * 100),
        genderRatio: this.calculateGenderRatio(filteredData),
        avgRating: 8.5,
        promotionRate: 12
      }
    },
    
    calculateGenderRatio(data) {
      const males = data.filter(item => item.personnel_etc_m_type === 'ชาย').length
      const females = data.filter(item => item.personnel_etc_m_type === 'หญิง').length
      if (females === 0) return 'N/A'
      const ratio = (males / females).toFixed(1)
      return `${ratio}:1`
    },
    
    // Chart Update Methods
    updateAllCharts() {
      this.updateRegionChart()
      this.updateSalaryDistributionChart()
      this.updateEducationCharts()
      this.updateAgeExperienceChart()
      this.updatePersonnelBudgetChart()
      this.updateLicenseStatusChart()
    },
    
    updateRegionChart() {
      const regionCounts = {}
      this.filteredPersonnelData.forEach(item => {
        const region = item.region_name || 'ไม่ระบุ'
        regionCounts[region] = (regionCounts[region] || 0) + 1
      })
      
      this.regionChartOptions = {
        ...this.regionChartOptions,
        labels: Object.keys(regionCounts)
      }
      this.regionChartData = Object.values(regionCounts)
      this.regionChartSeries = [{
        name: 'จำนวนบุคลากร',
        data: Object.values(regionCounts)
      }]
    },
    
    updateSalaryDistributionChart() {
      const salaryRanges = {
        '<20K': 0, '20-30K': 0, '30-40K': 0, 
        '40-50K': 0, '50-60K': 0, '>60K': 0
      }
      
      this.filteredPersonnelData.forEach(item => {
        const salary = item.personnel_etc_salary || 0
        if (salary < 20000) salaryRanges['<20K']++
        else if (salary < 30000) salaryRanges['20-30K']++
        else if (salary < 40000) salaryRanges['30-40K']++
        else if (salary < 50000) salaryRanges['40-50K']++
        else if (salary < 60000) salaryRanges['50-60K']++
        else salaryRanges['>60K']++
      })
      
      this.salaryDistributionSeries = [{
        name: 'จำนวนบุคลากร',
        data: Object.values(salaryRanges)
      }]
    },
    
    updateEducationCharts() {
      // Education levels
      const educationCounts = {}
      this.filteredPersonnelData.forEach(item => {
        const education = item.personnel_etc_education_1 || 'ไม่ระบุ'
        educationCounts[education] = (educationCounts[education] || 0) + 1
      })
      
      this.educationChartOptions = {
        ...this.educationChartOptions,
        yaxis: {
          ...this.educationChartOptions.yaxis,
          categories: Object.keys(educationCounts)
        }
      }
      this.educationChartSeries = [{
        name: 'จำนวนบุคลากร',
        data: Object.values(educationCounts)
      }]
      
      // Branches (Treemap)
      const branchCounts = {}
      this.filteredPersonnelData.forEach(item => {
        const branch = item.personnel_etc_branch || 'ไม่ระบุ'
        branchCounts[branch] = (branchCounts[branch] || 0) + 1
      })
      
      this.branchChartSeries = [{
        data: Object.keys(branchCounts).map(branch => ({
          x: branch,
          y: branchCounts[branch]
        }))
      }]
      
      // Subjects
      const subjectCounts = {}
      this.filteredPersonnelData.forEach(item => {
        const subject = item.personnel_etc_subject || 'ไม่ระบุ'
        subjectCounts[subject] = (subjectCounts[subject] || 0) + 1
      })
      
      this.subjectChartOptions = {
        ...this.subjectChartOptions,
        labels: Object.keys(subjectCounts)
      }
      this.subjectChartData = Object.values(subjectCounts)
    },
    
    updateAgeExperienceChart() {
      const data = this.filteredPersonnelData.map(item => {
        const age = this.calculateAge(item.personnel_etc_datebrith)
        const experience = item.experience || 0
        return [age, experience]
      }).filter(point => point[0] > 0 && point[1] >= 0)
      
      this.ageExperienceData = [{
        name: 'บุคลากร',
        data: data
      }]
    },
    
    updatePersonnelBudgetChart() {
      const budgetTypes = {}
      const personnelTypes = [...new Set(this.filteredPersonnelData.map(item => item.personnel_etc_type))]
      
      personnelTypes.forEach(type => {
        budgetTypes[type] = {
          'งบประมาณแผ่นดิน': 0,
          'งบประมาณเงินรายได้': 0,
          'งบประมาณอื่นๆ': 0
        }
      })
      
      this.filteredPersonnelData.forEach(item => {
        const type = item.personnel_etc_type || 'ไม่ระบุ'
        const budget = item.personnel_etc_budget || 'งบประมาณอื่นๆ'
        if (budgetTypes[type] && budgetTypes[type][budget] !== undefined) {
          budgetTypes[type][budget]++
        }
      })
      
      this.personnelBudgetOptions = {
        ...this.personnelBudgetOptions,
        xaxis: {
          ...this.personnelBudgetOptions.xaxis,
          categories: Object.keys(budgetTypes)
        }
      }
      
      this.personnelBudgetSeries = [
        {
          name: 'งบประมาณแผ่นดิน',
          data: Object.values(budgetTypes).map(item => item['งบประมาณแผ่นดิน'])
        },
        {
          name: 'งบประมาณเงินรายได้',
          data: Object.values(budgetTypes).map(item => item['งบประมาณเงินรายได้'])
        },
        {
          name: 'งบประมาณอื่นๆ',
          data: Object.values(budgetTypes).map(item => item['งบประมาณอื่นๆ'])
        }
      ]
    },
    
    updateLicenseStatusChart() {
      const statusCounts = {
        valid: 0,
        expired: 0,
        pending: 0,
        none: 0
      }
      
      this.filteredPersonnelData.forEach(item => {
        const status = item.license_status || 'none'
        statusCounts[status]++
      })
      
      const total = Object.values(statusCounts).reduce((a, b) => a + b, 0)
      this.licenseStatusData = [
        Math.round((statusCounts.valid / total) * 100),
        Math.round((statusCounts.expired / total) * 100),
        Math.round((statusCounts.pending / total) * 100),
        Math.round((statusCounts.none / total) * 100)
      ]
    },
    
    // Helper Methods
    calculateAge(birthDate) {
      if (!birthDate) return 0
      const today = new Date()
      const birth = new Date(birthDate)
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
      }
      return age > 0 ? age : 0
    },
    
    calculateExperience(appointDate) {
      if (!appointDate) return 0
      const today = new Date()
      const appoint = new Date(appointDate)
      const diffTime = Math.abs(today - appoint)
      const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25))
      return diffYears
    },
    
    getLicenseStatus(item) {
      if (!item.personnel_etc_licence) return 'none'
      if (!item.personnel_etc_endlicence) return 'pending'
      
      const endDate = new Date(item.personnel_etc_endlicence)
      const today = new Date()
      
      if (endDate < today) return 'expired'
      if (endDate - today < 30 * 24 * 60 * 60 * 1000) return 'pending' // หมดอายุใน 30 วัน
      return 'valid'
    },
    
    isInSalaryRange(salary, range) {
      if (!salary || !range) return true
      
      switch (range) {
        case 'below_20k': return salary < 20000
        case '20k_30k': return salary >= 20000 && salary < 30000
        case '30k_40k': return salary >= 30000 && salary < 40000
        case '40k_50k': return salary >= 40000 && salary < 50000
        case '50k_60k': return salary >= 50000 && salary < 60000
        case 'above_60k': return salary >= 60000
        default: return true
      }
    },
    
    getInitials(fullName) {
      if (!fullName) return '??'
      const names = fullName.trim().split(' ')
      if (names.length === 1) return names[0].charAt(0).toUpperCase()
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
    },
    
    getSalaryColor(salary) {
      if (!salary) return 'grey'
      if (salary < 25000) return 'red'
      if (salary < 35000) return 'orange'
      if (salary < 50000) return 'blue'
      return 'green'
    },
    
    getExperienceProgress(experience) {
      return Math.min((experience / 30) * 100, 100)
    },
    
    getExperienceColor(experience) {
      if (experience < 5) return 'red'
      if (experience < 15) return 'orange'
      if (experience < 25) return 'blue'
      return 'green'
    },
    
    getLicenseStatusText(status) {
      const statusMap = {
        'valid': 'ใช้ได้',
        'expired': 'หมดอายุ',
        'pending': 'รอต่ออายุ',
        'none': 'ไม่มี'
      }
      return statusMap[status] || 'ไม่ระบุ'
    },
    
    // Chart Toggle Methods
    toggleChartType(chartName) {
      if (chartName === 'region') {
        this.regionChartType = this.regionChartType === 'pie' ? 'bar' : 'pie'
        this.updateRegionChart()
      }
    },
    
    // Filter Methods
    toggleFilters() {
      this.showFilters = !this.showFilters
    },
    
    onFilterChange() {
      this.applyFilters()
    },
    
    applyFilters() {
      this.updateSummaryData()
      this.updateAllCharts()
      this.showNotification('ใช้ตัวกรองเรียบร้อย', 'success')
    },
    
    resetFilters() {
      this.filters = {
        region: null,
        province: null,
        personnelType: null,
        budget: null,
        education: null,
        level: null,
        workgroup: null,
        subject: null,
        nationality: null,
        salaryRange: null,
        dateRange: []
      }
      this.applyFilters()
      this.showNotification('ล้างตัวกรองเรียบร้อย', 'info')
    },
    
    async refreshData() {
      await this.loadData()
    },
    
    // Export Methods
    exportReport() {
      // สร้างข้อมูลสำหรับส่งออก
      const reportData = {
        summary: this.summaryData,
        personnel: this.filteredPersonnelData,
        filters: this.filters,
        timestamp: new Date().toISOString()
      }
      
      // สร้าง CSV content
      let csvContent = "data:text/csv;charset=utf-8,\uFEFF"
      csvContent += "รหัสสถานศึกษา,ชื่อ-นามสกุล,ตำแหน่ง,ประเภท,เงินเดือน,วุฒิการศึกษา,สาขา,วิชาเอก,จังหวัด,ภูมิภาค,วันที่บรรจุ,อายุ,ประสบการณ์,สถานะใบอนุญาต\n"
      
      this.filteredPersonnelData.forEach(item => {
        const row = [
          item.personnel_etc_college_code || '',
          item.fullName || '',
          item.personnel_etc_id_position || '',
          item.personnel_etc_type || '',
          item.personnel_etc_salary || '',
          item.personnel_etc_education_1 || '',
          item.personnel_etc_branch || '',
          item.personnel_etc_subject || '',
          item.province_name || '',
          item.region_name || '',
          item.personnel_etc_dateapp || '',
          this.calculateAge(item.personnel_etc_datebrith),
          item.experience || '',
          this.getLicenseStatusText(item.license_status)
        ]
        csvContent += row.map(field => `"${field}"`).join(',') + '\n'
      })
      
      // ดาวน์โหลดไฟล์
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `รายงานอัตรากำลัง_${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.showNotification('ส่งออกรายงานสำเร็จ', 'success')
    },
    
    // Notification method
    showNotification(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  },
  
  // Watch for filter changes
  watch: {
    filteredPersonnelData: {
      handler() {
        this.updateSummaryData()
        this.updateAllCharts()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.header-card {
  background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%);
  color: white;
}

.filter-card {
  overflow: hidden;
}

.filter-header {
  background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%);
}

.dashboard-card {
  transition: all 0.3s ease;
  border-radius: 16px;
  overflow: hidden;
}

.dashboard-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.15) !important;
}

.stat-number {
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1;
  color: white;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.95;
  color: white;
  margin-bottom: 4px;
}

.growth-indicator {
  font-size: 0.75rem;
  opacity: 0.8;
  color: white;
}

.insight-number {
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
}

.insight-label {
  font-size: 0.8rem;
  color: white;
  opacity: 0.9;
}

.summary-card {
  background: linear-gradient(135deg, #d9777b 0%, #f5576c 100%);
  color: white;
}

.region-card {
  background: linear-gradient(135deg, #90a4ae 0%, #455a64 100%);
  color: white;
}

.education-card {
  background: linear-gradient(135deg, #2e7d32 0%, #2e7d32 100%);
  color: white;
}

.budget-card {
  background: linear-gradient(135deg, #d9777b 0%, #e6b04d 100%);
  color: white;
}

.insight-card-1 {
  background: linear-gradient(135deg, #90a4ae 0%, #fbecec 100%);
  color: white;
}

.insight-card-2 {
  background: linear-gradient(135deg, #fff4dc 0%, #fcb69f 100%);
  color: white;
}

.insight-card-3 {
  background: linear-gradient(135deg, #ff9a9e 0%, #fbecec 100%);
  color: white;
}

.insight-card-4 {
  background: linear-gradient(135deg, #d9777b 0%, #fbecec 100%);
  color: white;
}

.chart-container {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
}

.data-table-card {
  background: #fff;
  border-radius: 16px;
}

.search-field {
  max-width: 300px;
}

.custom-tooltip {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
}

/* Enhanced V-tabs styling */
.v-tabs-items {
  background: transparent;
}

/* Enhanced table styling */
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.v-data-table >>> th {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  border-bottom: 2px solid #e9ecef !important;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(102, 126, 234, 0.04) !important;
}

.v-data-table >>> .v-data-table__expanded {
  background-color: #f8f9fa;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stat-number {
    font-size: 1.8rem;
  }
  
  .insight-number {
    font-size: 1.4rem;
  }
  
  .dashboard-card:hover {
    transform: translateY(-2px);
  }
}

@media (max-width: 600px) {
  .stat-number {
    font-size: 1.5rem;
  }
  
  .insight-number {
    font-size: 1.2rem;
  }
  
  .stat-label, .insight-label {
    font-size: 0.75rem;
  }
}

/* Loading animation */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Enhanced shadows */
.elevation-4 {
  box-shadow: 0 6px 12px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08) !important;
}

.elevation-6 {
  box-shadow: 0 8px 16px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.10) !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #b3151b 0%, #b3151b 100%);
}

/* Chart loading states */
.text-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* Enhanced filter section */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Better button styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
}

/* Enhanced chip styling */
.v-chip {
  font-weight: 500;
  border-radius: 12px;
}

/* Avatar styling */
.v-avatar {
  border: 2px solid rgba(255, 255, 255, 0.1);
}
</style>